import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, CreditCard, X } from 'lucide-react';
import { Button } from './ui/button';
import { apiRequest } from '../lib/utils';
import { cn } from '../lib/utils';

export const TrialBanner = () => {
  const [trialStatus, setTrialStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTrialStatus();
    
    // Atualizar a cada 5 minutos
    const interval = setInterval(fetchTrialStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchTrialStatus = async () => {
    try {
      const data = await apiRequest('/trial/status');
      setTrialStatus(data);
    } catch (error) {
      console.error('Erro ao buscar status do trial:', error);
    } finally {
      setLoading(false);
    }
  };

  // Não mostrar se está carregando, foi descartado, não está em trial, já pagou, ou é conta demo permanente
  if (loading || dismissed || !trialStatus?.is_trial || trialStatus?.has_paid || trialStatus?.is_permanent_trial) {
    return null;
  }

  // Se o trial expirou, mostrar banner de bloqueio
  if (trialStatus.trial_expired) {
    return (
      <div className="bg-red-600 text-white px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">
              Seu período de teste expirou! Assine agora para continuar usando o sistema.
            </span>
          </div>
          <Button 
            onClick={() => navigate('/assinatura')}
            className="bg-white text-red-600 hover:bg-red-50"
            size="sm"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Assinar Agora
          </Button>
        </div>
      </div>
    );
  }

  // Se deve mostrar aviso (6, 3, 1 dias ou menos)
  if (trialStatus.show_warning || trialStatus.days_remaining <= 3) {
    const isUrgent = trialStatus.days_remaining <= 1;
    
    return (
      <div className={cn(
        "px-4 py-2.5 transition-all",
        isUrgent ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
      )}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Clock className={cn("w-5 h-5 flex-shrink-0", isUrgent ? "animate-pulse" : "")} />
            <span className="font-medium text-sm">
              {trialStatus.days_remaining === 0 
                ? "Seu período de teste expira HOJE!" 
                : trialStatus.days_remaining === 1
                  ? "Seu período de teste expira amanhã!"
                  : `Seu período de teste expira em ${trialStatus.days_remaining} dias.`
              }
              {' '}Assine para não perder o acesso.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate('/assinatura')}
              className={cn(
                "text-sm",
                isUrgent 
                  ? "bg-white text-amber-600 hover:bg-amber-50" 
                  : "bg-amber-600 text-white hover:bg-amber-700"
              )}
              size="sm"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Ver Planos
            </Button>
            {!isUrgent && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDismissed(true)}
                className="text-amber-700 hover:text-amber-900 dark:text-amber-300"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mostrar banner informativo normal
  return (
    <div className="bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 px-4 py-2">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">
            <strong>Período de teste:</strong> {trialStatus.days_remaining} dias restantes
            {trialStatus.item_limit && (
              <span className="ml-2 text-teal-600 dark:text-teal-400">
                • Limite: {trialStatus.item_limit} itens por categoria
              </span>
            )}
          </span>
        </div>
        <Button 
          variant="outline"
          onClick={() => navigate('/assinatura')}
          className="text-sm border-teal-300 text-teal-700 hover:bg-teal-100 dark:border-teal-700 dark:text-teal-300"
          size="sm"
        >
          Ver Planos
        </Button>
      </div>
    </div>
  );
};

export default TrialBanner;
